import { useEffect, useSyncExternalStore, type ReactNode } from "react";
import { getAntiforgeryToken } from "../api/http";
import { formatText } from "../i18n/texts";
import { simulatorState, subscribeSimulator, toggleSimulator } from "../simulator/launch";
import { BheliLogo } from "../ui/brand";
import { ShellIcon } from "../ui/icons/Icon";
import { portalHref } from "./routes";
import {
  badge,
  contextItems,
  crmEntryPath,
  headerContext,
  homePath,
  iconFor,
  initials,
  navClass,
  routeFlags,
  showGlobalNavigation,
  type ContextItem,
  type Label,
  type ShellData,
} from "./shellModel";

// Shell del Company Portal con el mismo HTML que PortalLayout.razor + PortalHeader.razor + HeaderContextNav.razor +
// ContextBarMobile.razor. El comportamiento (cajón, riel, menús, «Más», tooltips) lo da feedback.js del portal actual,
// que trabaja sobre estos mismos atributos data-shell-*; aquí se le avisa con «enhancedload» después de cada pintado.

type Props = {
  data: ShellData;
  L: Label;
  locale: string;
  path: string;
  search: string;
  children: ReactNode;
  reportViews?: ReactNode;
  /** La isla del chat (Dock y chat dedicado) que vive en #bheli-chat-root, como en PortalLayout.razor. */
  chatRoot?: ReactNode;
  /**
   * El simulador (ventana permanente). Va en #bheli-simulator-root, fuera de .app-shell: el shell queda igual al del portal
   * actual y bheli-chat.js (que atiende #bheli-simulation-surface del panel Blazor) no intercepta la herramienta.
   */
  simulationRoot?: ReactNode;
};

/** Avisa a feedback.js y al tooltip del shell que el documento cambió (como la navegación mejorada de Blazor). */
export function useEnhancedLoad(...dependencies: unknown[]): void {
  useEffect(() => {
    document.dispatchEvent(new Event("enhancedload"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}

function ContextBarMobile({
  title,
  current,
  L,
  items,
}: {
  title: string;
  current: string;
  L: Label;
  items: ContextItem[];
}) {
  if (!title.trim()) return null;
  const same = title === current;
  return (
    <div className="context-bar-mobile">
      <details className="context-sheet-toggle" data-shell-disclosure="">
        <summary
          className="context-bar-summary"
          aria-label={
            same
              ? `${L("SHELL_CONTEXT_NAVIGATION")}: ${title}`
              : `${L("SHELL_CONTEXT_NAVIGATION")}: ${title} · ${current}`
          }
        >
          <span className="context-bar-text">
            {!same ? (
              <>
                <span className="context-parent-name">{title}</span>
                <span className="context-sep" aria-hidden="true">
                  ·
                </span>
              </>
            ) : null}
            <span className="context-section-name">{current}</span>
          </span>
          <ShellIcon name="chevron-down" className="context-chevron" />
        </summary>
        <div className="context-sheet">
          <div className="context-sheet-header">
            <strong>{title}</strong>
            <span>{L("SHELL_CONTEXT_SECTIONS")}</span>
            {L("SHELL_CLOSE").trim() ? (
              <button type="button" className="button secondary context-sheet-close" data-shell-disclosure-close="">
                {L("SHELL_CLOSE")}
              </button>
            ) : null}
          </div>
          <nav className="context-sheet-links" aria-label={L("SHELL_CONTEXT_NAVIGATION")}>
            {items.map((item) => (
              <a
                key={item.href}
                className={`context-sheet-link ${item.isActive ? "active" : ""}`}
                href={portalHref(item.href)}
                aria-current={item.isActive ? "page" : undefined}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </div>
      </details>
    </div>
  );
}

function HeaderContextNav({ title, icon, items, L }: { title: string; icon: string; items: ContextItem[]; L: Label }) {
  return (
    <div className="shell-header-context" data-header-context="">
      <div className="shell-context-row" data-context-desktop="">
        <span className="shell-context-name">
          <ShellIcon name={icon} />
          {title}
        </span>
        <nav className="shell-context-visible" aria-label={L("SHELL_CONTEXT_NAVIGATION")} data-context-visible="">
          {items.map((item) => (
            <a
              key={item.href}
              className={`context-link ${item.isActive ? "active" : ""}`}
              href={portalHref(item.href)}
              aria-current={item.isActive ? "page" : undefined}
            >
              <ShellIcon name={iconFor(item.href)} />
              <span>{item.label}</span>
            </a>
          ))}
        </nav>
        <details className="shell-context-overflow" data-context-overflow="" data-shell-disclosure="" hidden>
          <summary aria-expanded="false">
            {L("SHELL_NAV_MORE")} <ShellIcon name="chevron-down" />
          </summary>
          <nav className="shell-popover shell-overflow-links" aria-label={L("SHELL_CONTEXT_NAVIGATION")}>
            {items.map((item) => (
              <a
                key={item.href}
                className={`shell-menu-item ${item.isActive ? "active" : ""}`}
                href={portalHref(item.href)}
                aria-current={item.isActive ? "page" : undefined}
              >
                <ShellIcon name={iconFor(item.href)} />
                {item.label}
              </a>
            ))}
          </nav>
        </details>
      </div>
      {items.length > 0 ? (
        <ContextBarMobile
          title={title}
          current={items.find((item) => item.isActive)?.label ?? title}
          L={L}
          items={items}
        />
      ) : (
        <span className="shell-context-compact-name">{title}</span>
      )}
    </div>
  );
}

/** Salir: revoca en Identity (POST /logout con el token) y vuelve al ingreso; el formulario queda igual al de hoy. */
async function signOut(event: React.FormEvent<HTMLFormElement>) {
  event.preventDefault();
  const response = await fetch("/logout", {
    method: "POST",
    credentials: "same-origin",
    headers: { "X-CSRF-TOKEN": getAntiforgeryToken() ?? "" },
  });
  if (response.ok) window.location.assign("/login");
}

function PortalHeader({
  data,
  L,
  path,
  search,
  globalMenu,
  context,
  items,
  home,
}: {
  data: ShellData;
  L: Label;
  path: string;
  search: string;
  globalMenu: boolean;
  context: { title: string; icon: string };
  items: ContextItem[];
  home: string;
}) {
  const currentPage = `${location.origin}${path}${search}`;
  const simulator = useSyncExternalStore(subscribeSimulator, simulatorState);
  // Minimizado con novedades: el nombre de la herramienta las dice («Simulador · 1 novedad»).
  const simulatorLabel =
    simulator.minimized && simulator.activity > 0
      ? `${L("SIM_GLOBAL_TITLE")} · ${simulator.activity === 1 ? L("SIM_ACTIVITY_ONE") : formatText(L("SIM_ACTIVITY_COUNT"), simulator.activity)}`
      : L("SIM_GLOBAL_TITLE");
  const tools = data.employeesEnabled
    ? [
        {
          id: "simulador",
          label: simulatorLabel,
          icon: "flask-conical",
          action: "/portal/simulacion",
          isActive: simulator.open,
        },
      ]
    : [];
  const token = getAntiforgeryToken() ?? "";
  const profileName = data.profileName.trim() ? data.profileName : L("NAV_ACCOUNT");
  const profileInitials = data.profileInitials.trim()
    ? data.profileInitials
    : profileName.substring(0, 1).toUpperCase();
  return (
    <header className="topbar shell-topbar">
      {globalMenu ? (
        <a
          href={`${currentPage}#portal-navigation`}
          className="menu-button"
          data-shell-open="portal-navigation"
          aria-controls="portal-navigation"
          aria-expanded="true"
          aria-label={L("NAV_MENU")}
          title={L("NAV_MENU")}
        >
          <ShellIcon name="menu" />
        </a>
      ) : null}
      <a
        className="brand shell-brand bheli-brand"
        href={portalHref(home)}
        aria-label={home === "/portal" ? L("SHELL_BRAND_HOME") : context.title}
      >
        <BheliLogo variant="horizontal" size={24} theme="auto" />
      </a>
      <span className="shell-divider" aria-hidden="true"></span>
      <HeaderContextNav title={context.title} icon={context.icon} items={items} L={L} />
      <div className="shell-header-actions">
        <div className="shell-tools" role="toolbar" aria-label={L("SHELL_QUICK_TOOLS")}>
          {tools.map((tool) => (
            <a
              key={tool.id}
              href={portalHref(tool.action)}
              className={`shell-tool ${tool.isActive ? "active" : ""}`}
              data-simulation-launch={tool.id === "simulador" ? "" : undefined}
              role="button"
              aria-label={tool.label}
              aria-pressed={tool.isActive ? "true" : "false"}
              data-shell-tooltip={tool.label}
              onClick={(event) => {
                // El simulador se abre encima de la pantalla actual, sin navegar (como data-simulation-launch).
                if (tool.id !== "simulador" || event.ctrlKey || event.metaKey || event.shiftKey || event.button !== 0)
                  return;
                event.preventDefault();
                toggleSimulator();
              }}
            >
              <ShellIcon name={tool.icon} />
              {tool.id === "simulador" && simulator.minimized && simulator.activity > 0 ? (
                <span className="shell-tool-badge" aria-hidden="true">
                  {simulator.activity > 99 ? "99+" : simulator.activity}
                </span>
              ) : null}
            </a>
          ))}
        </div>
        <span className="shell-divider" aria-hidden="true"></span>
        <details className="user-menu" data-shell-disclosure="" data-shell-menu="">
          <summary
            className="user-trigger"
            role="button"
            aria-label={L("NAV_ACCOUNT")}
            aria-haspopup="menu"
            aria-expanded="false"
            data-shell-tooltip={profileName}
            data-tooltip-truncated=".user-trigger-name"
          >
            <span className="user-avatar" aria-hidden="true">
              {profileInitials}
            </span>
            <span className="user-trigger-name">{profileName}</span>
            <ShellIcon name="chevron-down" className="shell-chevron" />
          </summary>
          <div className="shell-popover user-popover" role="menu" aria-label={L("NAV_ACCOUNT")}>
            <div className="shell-user-identity">
              <span className="user-avatar" aria-hidden="true">
                {profileInitials}
              </span>
              <div>
                <strong>{profileName}</strong>
                <span>{data.organizationName}</span>
              </div>
            </div>
            {data.isSupport ? (
              <span className="shell-menu-context">{L("CP_SUPPORT_SCOPE_FIXED")}</span>
            ) : data.companies.length > 1 ? (
              <>
                <div className="shell-menu-heading">{L("SHELL_SWITCH_COMPANY")}</div>
                {data.companies.map((company) => (
                  <form key={company.id} method="post" action="/portal/organizaciones" data-company-switch="">
                    <input name="__RequestVerificationToken" type="hidden" value={token} />
                    <input type="hidden" name="_handler" value="organizaciones" />
                    <input type="hidden" name="form.OrganizationId" value={company.id} />
                    <input type="hidden" name="form.ReturnUrl" value={`${path}${search}`} />
                    <button
                      type="submit"
                      className="shell-menu-item company-option"
                      role="menuitemradio"
                      aria-checked={company.isActive ? "true" : "false"}
                    >
                      <span className="company-avatar" aria-hidden="true">
                        {initials(company.name)}
                      </span>
                      <span className="shell-option-name">{company.name}</span>
                      {company.isActive ? <ShellIcon name="check" /> : null}
                    </button>
                  </form>
                ))}
              </>
            ) : null}
            {data.settingsEnabled ? (
              <a className="shell-menu-item" href={portalHref("/portal/configuracion/empresa")} role="menuitem">
                <ShellIcon name="building-2" />
                {L("SHELL_COMPANY_SETTINGS")}
              </a>
            ) : null}
            <div className="shell-menu-divider" role="separator"></div>
            <a className="shell-menu-item" href={portalHref("/portal/perfil")} role="menuitem">
              <ShellIcon name="user" />
              {L("NAV_PROFILE")}
            </a>
            <a className="shell-menu-item" href={portalHref("/portal/preferencias")} role="menuitem">
              <ShellIcon name="settings" />
              {L("NAV_PREFS")}
            </a>
            <form method="post" action="/logout" onSubmit={signOut}>
              <input name="__RequestVerificationToken" type="hidden" value={token} />
              <button type="submit" className="shell-menu-item" role="menuitem">
                <ShellIcon name="log-out" />
                {L("ACCOUNT_SIGN_OUT")}
              </button>
            </form>
          </div>
        </details>
      </div>
    </header>
  );
}

export function Shell({ data, L, locale, path, search, children, reportViews, chatRoot, simulationRoot }: Props) {
  const flags = routeFlags(path, search);
  const globalMenu = showGlobalNavigation(data);
  const context = headerContext(data, flags, L);
  const items = contextItems(data, flags, L);
  const home = homePath(data, flags, L);
  const currentPage = `${location.origin}${path}${search}`;
  const approvalsName =
    data.pendingApprovals && data.pendingApprovals > 0
      ? `${L("NAV_APPROVALS")}, ${formatText(L("SHELL_PENDING_COUNT"), data.pendingApprovals)}`
      : L("NAV_APPROVALS");
  const approvalsTip =
    data.pendingApprovals && data.pendingApprovals > 0
      ? `${L("NAV_APPROVALS")} · ${formatText(L("SHELL_PENDING_COUNT"), data.pendingApprovals)}`
      : L("NAV_APPROVALS");
  const nav = (key: string) => navClass(key, flags);
  const current = (key: string) => (nav(key) === "nav-link active" ? "page" : undefined);
  const mainClass = `wrap ${flags.isChatSection ? "chat-section-main" : ""} ${flags.isDedicatedChat ? "chat-main" : flags.isSimulator ? "simulator-main" : ""}`;
  useEnhancedLoad(path, search, data);
  return (
    <>
      <div
        className="app-shell"
        data-company-shell=""
        data-report-scope={data.reportScope}
        data-report-locale={locale}
        data-single-destination={!globalMenu ? "true" : "false"}
      >
        <a className="skip" href={`${currentPage}#main`}>
          {L("A11Y_SKIP")}
        </a>
        <div className="shell-header">
          {data.isSupport ? (
            <div className="support-banner" role="region" aria-label={L("SUPPORT_BANNER")}>
              <span>
                <strong>{L("SUPPORT_BANNER")}</strong> ·{" "}
                {formatText(L("SUPPORT_BANNER_VIEWING"), data.supportOrganization ?? "", data.supportSubject ?? "")}
              </span>
              <form method="post" action="/soporte/salir">
                <input name="__RequestVerificationToken" type="hidden" value={getAntiforgeryToken() ?? ""} />
                <button type="submit" className="button secondary">
                  {L("SUPPORT_BANNER_END")}
                </button>
              </form>
            </div>
          ) : null}
          <PortalHeader
            data={data}
            L={L}
            path={path}
            search={search}
            globalMenu={globalMenu}
            context={context}
            items={items}
            home={home}
          />
        </div>
        <div className="shell-workspace">
          {globalMenu ? (
            <dialog
              id="portal-navigation"
              className="sidebar"
              role="complementary"
              open
              data-shell-dialog=""
              aria-label={L("NAV_MENU")}
            >
              <div className="shell-drawer-heading">
                <BheliLogo variant="horizontal" size={24} theme="auto" />
                <button type="button" className="shell-close" data-shell-close="" aria-label={L("SHELL_CLOSE")}>
                  <ShellIcon name="x" />
                </button>
              </div>
              <nav id="portal-primary-nav" aria-label={L("CP_SHELL_PRIMARY_NAV")}>
                {data.homeEnabled ? (
                  <a
                    className={nav("home")}
                    aria-current={current("home")}
                    href={portalHref("/portal")}
                    data-rail-tooltip={L("NAV_HOME")}
                  >
                    <ShellIcon name="house" className="nav-icon" />
                    <span className="nav-label">{L("NAV_HOME")}</span>
                  </a>
                ) : null}
                {data.chatEnabled || data.contactsEnabled ? (
                  <a
                    className={nav("chat")}
                    aria-current={current("chat")}
                    href={portalHref(data.chatEnabled ? "/portal/chat" : "/portal/chat/contactos")}
                    data-rail-tooltip={L("NAV_CHAT")}
                  >
                    <ShellIcon name="message-square-text" className="nav-icon" />
                    <span className="nav-label">{L("NAV_CHAT")}</span>
                  </a>
                ) : null}
                {data.employeesEnabled ? (
                  <>
                    <a
                      className={nav("empleados")}
                      aria-current={current("empleados")}
                      href={portalHref("/portal/empleados")}
                      data-rail-tooltip={L("NAV_EMPLOYEES")}
                    >
                      <ShellIcon name="user-cog" className="nav-icon" />
                      <span className="nav-label">{L("NAV_EMPLOYEES")}</span>
                    </a>
                    <a
                      className={nav("conocimiento")}
                      aria-current={current("conocimiento")}
                      href={portalHref("/portal/conocimiento")}
                      data-rail-tooltip={L("NAV_KNOWLEDGE")}
                    >
                      <ShellIcon name="book-open" className="nav-icon" />
                      <span className="nav-label">{L("NAV_KNOWLEDGE")}</span>
                    </a>
                    <a
                      className={nav("aprobaciones")}
                      aria-current={current("aprobaciones")}
                      href={portalHref("/portal/aprobaciones")}
                      data-rail-tooltip={approvalsTip}
                      aria-label={approvalsName}
                    >
                      <ShellIcon name="list-checks" className="nav-icon" />
                      <span className="nav-label">{L("NAV_APPROVALS")}</span>
                      {data.pendingApprovals && data.pendingApprovals > 0 ? (
                        <>
                          <span className="shell-nav-badge" aria-hidden="true">
                            {badge(data.pendingApprovals)}
                          </span>
                          <span className="shell-nav-dot" aria-hidden="true"></span>
                        </>
                      ) : null}
                    </a>
                    <a
                      className={nav("operacion")}
                      aria-current={current("operacion")}
                      href={portalHref("/portal/operacion")}
                      data-rail-tooltip={L("NAV_OPERATION")}
                    >
                      <ShellIcon name="layout-dashboard" className="nav-icon" />
                      <span className="nav-label">{L("NAV_OPERATION")}</span>
                    </a>
                  </>
                ) : null}
                {data.crmEnabled ? (
                  <a
                    className={`nav-link ${flags.isCrmSection ? "active" : ""}`}
                    aria-current={flags.isCrmSection ? "page" : undefined}
                    href={portalHref(crmEntryPath(data, flags, L))}
                    data-rail-tooltip={L("CRM_TITLE")}
                  >
                    <ShellIcon name="contact" className="nav-icon" />
                    <span className="nav-label">{L("CRM_TITLE")}</span>
                  </a>
                ) : null}
                {data.erpEnabled ? (
                  <a
                    className={nav("erp")}
                    aria-current={current("erp")}
                    href={portalHref("/erp")}
                    data-rail-tooltip={L("NAV_ERP")}
                  >
                    <ShellIcon name="package" className="nav-icon" />
                    <span className="nav-label">{L("NAV_ERP")}</span>
                  </a>
                ) : null}
                {data.settingsEnabled ? (
                  <a
                    className={nav("configuracion")}
                    aria-current={current("configuracion")}
                    href={portalHref("/portal/configuracion")}
                    data-rail-tooltip={L("NAV_SETTINGS")}
                  >
                    <ShellIcon name="settings" className="nav-icon" />
                    <span className="nav-label">{L("NAV_SETTINGS")}</span>
                  </a>
                ) : null}
              </nav>
            </dialog>
          ) : null}
          <div className="shell-main">
            <main id="main" tabIndex={-1} className={mainClass}>
              {children}
            </main>
            <div id="bheli-report-views" data-permanent="">
              {reportViews}
            </div>
            <div id="bheli-chat-root" data-permanent="">
              {chatRoot}
            </div>
          </div>
        </div>
      </div>
      {simulationRoot ? (
        <div id="bheli-simulator-root" data-permanent="">
          {simulationRoot}
        </div>
      ) : null}
    </>
  );
}
